import type { AccountInfo } from '@azure/msal-browser';
import posthog from 'posthog-js';

type B2CClaims = {
  emails?: string[];
  email?: string;
  preferred_username?: string;
  signInName?: string;
  signInNames?: { emailAddress?: string };
  name?: string;
  given_name?: string;
  family_name?: string;
  extension_companyid?: string;
  extension_companyRegisteredName?: string;
  extension_roleIds?: string;
} & Record<string, unknown>;

const looksLikeEmail = (s: unknown): s is string =>
  typeof s === 'string' && /.+@.+\..+/.test(s);

const emailFromAccount = (account: AccountInfo): string | undefined => {
  const claims = (account.idTokenClaims ?? {}) as B2CClaims;
  const candidates: unknown[] = [
    claims.emails?.[0],
    claims.email,
    claims.preferred_username,
    claims.signInName,
    claims.signInNames?.emailAddress,
    account.username
  ];
  return candidates.find(looksLikeEmail);
};

export const identifyUserFromAccount = (account: AccountInfo) => {
  if (!posthog.__loaded) return;

  const claims = (account.idTokenClaims ?? {}) as B2CClaims;
  const email = emailFromAccount(account);
  const companyId = claims.extension_companyid;
  const roleIds = claims.extension_roleIds?.split(',').map((r) => r.trim());

  posthog.identify(account.localAccountId || account.homeAccountId, {
    email,
    name: claims.name ?? account.name,
    given_name: claims.given_name,
    family_name: claims.family_name,
    tenant_id: account.tenantId,
    company_id: companyId,
    company_name: claims.extension_companyRegisteredName,
    role_ids: roleIds
  });

  if (companyId) {
    posthog.group('company', companyId, {
      company_id: companyId,
      name: claims.extension_companyRegisteredName
    });
  }
};

export const registerSuperProperties = () => {
  if (!posthog.__loaded) return;

  posthog.register({
    environment: import.meta.env.VITE_ENVIRONMENT,
    deployment_version: import.meta.env.VITE_DEPLOYMENT_VERSION
  });
};

export const resetIdentity = () => {
  if (!posthog.__loaded) return;
  posthog.reset();
};
